import { useQuery } from '@tanstack/react-query';

import { useAuthHydration } from '@/hooks/use-auth-hydration';
import api from '@/services/api';
import {
  PaymentBalance,
  PaymentReleasing,
  PaymentSchedule,
  PaymentSummary,
} from '@/types/payment-reporting';

export const paymentReportingKeys = {
  all: ['payment-reporting'] as const,
  balance: () => [...paymentReportingKeys.all, 'balance'] as const,
  schedule: () => [...paymentReportingKeys.all, 'schedule'] as const,
  releasing: () => [...paymentReportingKeys.all, 'releasing'] as const,
  summary: (from: string, to: string) =>
    [...paymentReportingKeys.all, 'summary', from, to] as const,
};

/**
 * `GET /payment-account/balance` (T3.2, T6.1): os três números do topo de
 * Recebimentos. 403 (quem não pode ver) e 404 (conta ainda não ativada)
 * chegam em `error` — quem chama decide o que mostrar (`getApiErrorMessage`).
 */
export function usePaymentBalance(options?: { enabled?: boolean }) {
  const isHydrated = useAuthHydration();

  return useQuery({
    queryKey: paymentReportingKeys.balance(),
    queryFn: async () => (await api.get<PaymentBalance>('/payment-account/balance')).data,
    enabled: isHydrated && options?.enabled !== false,
  });
}

/** `GET /payment-account/schedule` (T3.2, T6.1): agenda dos próximos dias. */
export function usePaymentSchedule(options?: { enabled?: boolean }) {
  const isHydrated = useAuthHydration();

  return useQuery({
    queryKey: paymentReportingKeys.schedule(),
    queryFn: async () => (await api.get<PaymentSchedule>('/payment-account/schedule')).data,
    enabled: isHydrated && options?.enabled !== false,
  });
}

/** `GET /payment-account/releasing`: cada cobrança que ainda vai liberar, com a data. */
export function usePaymentReleasing(options?: { enabled?: boolean }) {
  const isHydrated = useAuthHydration();

  return useQuery({
    queryKey: paymentReportingKeys.releasing(),
    queryFn: async () => (await api.get<PaymentReleasing>('/payment-account/releasing')).data,
    enabled: isHydrated && options?.enabled !== false,
  });
}

/** `GET /payment-account/summary?from&to` (T3.2, T6.1): resumo do período, mesmo seletor de `reports`. */
export function usePaymentSummary(from: string, to: string, options?: { enabled?: boolean }) {
  const isHydrated = useAuthHydration();

  return useQuery({
    queryKey: paymentReportingKeys.summary(from, to),
    queryFn: async () =>
      (
        await api.get<PaymentSummary>('/payment-account/summary', {
          params: { from, to },
        })
      ).data,
    enabled: isHydrated && !!from && !!to && options?.enabled !== false,
  });
}
